import type { SendMessageBatchRequestEntry, SQS } from '@aws-sdk/client-sqs';
import { fixStackTrace } from './utils/ErrorUtils';

export type QueuedScheduledAction = {
    id: string;
    actionName: string;
    actionArgs: unknown;
    userId: string;
    isSystemAction?: boolean;
};

export type BatchResult = { successful: string[]; failed: string[] };

export type ScheduledActionsQueue = {
    enqueueScheduledActions: (actions: QueuedScheduledAction[]) => Promise<BatchResult>;
};

export function createScheduledActionsQueue(opts: { sqs: SQS; queueUrl: string }): ScheduledActionsQueue {
    const { sqs, queueUrl } = opts;

    const queue: ScheduledActionsQueue = {
        enqueueScheduledActions: async (actions) => {
            const messages = actions.map((action) => ({ id: action.id, body: action }));

            return sendMessageBatch(sqs, queueUrl, messages);
        },
    };

    return queue;
}

export function createMockScheduledActionsQueue(): ScheduledActionsQueue {
    return {
        enqueueScheduledActions: async () => ({ successful: [], failed: [] }),
    };
}

/**
 * Send messages to SQS in concurrent batches of 10, retrying failed messages up to `retryCount` times.
 */
export async function sendMessageBatch(
    sqs: SQS,
    queueUrl: string,
    messages: { id: string; body: {} }[],
    retryCount = 3,
): Promise<BatchResult> {
    const batches: SendMessageBatchRequestEntry[][] = [];

    const unbatchedMessages = [...messages];
    while (unbatchedMessages.length > 0) {
        batches.push(
            unbatchedMessages.splice(0, 10).map((message) => ({
                Id: message.id, // identifies message within batch: not a globally unique ID
                MessageBody: JSON.stringify(message.body),
            })),
        );
    }

    const resultsPerBatch = await Promise.all(
        batches.map(async (batch) => {
            const { Successful, Failed } = await sqs
                .sendMessageBatch({
                    Entries: batch,
                    QueueUrl: queueUrl,
                })
                .catch((error) => {
                    error.Entries = batch;
                    throw fixStackTrace(error);
                });

            return {
                successful: (Successful ?? []).map(({ Id }) => Id!),
                failed: (Failed ?? []).map(({ Id }) => Id!),
            };
        }),
    );

    const result = resultsPerBatch.reduce(
        (acc, { successful, failed }) => {
            acc.successful.push(...successful);
            acc.failed.push(...failed);
            return acc;
        },
        { successful: [], failed: [] },
    );

    if (result.failed.length > 0 && retryCount > 0) {
        const retryResult = await sendMessageBatch(
            sqs,
            queueUrl,
            messages.filter(({ id }) => result.failed.includes(id)),
            retryCount - 1,
        );

        result.successful.push(...retryResult.successful);
        result.failed = retryResult.failed;
    }

    return result;
}
